class BetaCharts extends Tango.Component {
	init() {
		this._margin = { top: 20, right: 30, bottom: 40, left: 50 };
	}

	widgetDidMount() {
		this._loadD3(() => {
			this._renderScatter();
			if (this.state.rollingBetas && this.state.rollingBetas.length > 0) {
				this._renderRolling();
			}
		});
	}

	// ══════════════════════════════════════════════
	//  D3 loader (CDN)
	// ══════════════════════════════════════════════

	_loadD3(callback) {
		if (window.d3) {
			callback();
			return;
		}
		const script = document.createElement('script');
		script.src = 'https://cdnjs.cloudflare.com/ajax/libs/d3/7.9.0/d3.min.js';
		script.onload = callback;
		document.head.appendChild(script);
	}

	// ══════════════════════════════════════════════
	//  Scatter Plot: stock returns vs benchmark returns
	// ══════════════════════════════════════════════

	_renderScatter() {
		const container = document.querySelector('.beta-chart-container[data-chart-type="scatter"]');
		if (!container || !this.state.stockReturns || this.state.stockReturns.length === 0) return;

		const stockRet = this.state.stockReturns;
		const benchRet = this.state.benchReturns;
		const alpha = this.state.alpha;
		const beta = this.state.beta;

		const width = container.clientWidth || 600;
		const height = 400;
		const m = this._margin;
		const w = width - m.left - m.right;
		const h = height - m.top - m.bottom;

		const svg = d3.select(container)
			.append('svg')
			.attr('width', width)
			.attr('height', height)
			.append('g')
			.attr('transform', `translate(${m.left},${m.top})`);

		// Scales
		const xExtent = d3.extent(benchRet);
		const yExtent = d3.extent(stockRet);
		const pad = 0.005;

		const xScale = d3.scaleLinear()
			.domain([xExtent[0] - pad, xExtent[1] + pad])
			.range([0, w]);

		const yScale = d3.scaleLinear()
			.domain([yExtent[0] - pad, yExtent[1] + pad])
			.range([h, 0]);

		// Axes
		svg.append('g')
			.attr('transform', `translate(0,${h})`)
			.call(d3.axisBottom(xScale).ticks(8).tickFormat(d3.format('.1%')))
			.append('text')
			.attr('x', w / 2).attr('y', 35)
			.attr('fill', 'currentColor')
			.attr('text-anchor', 'middle')
			.text('Benchmark Return');

		svg.append('g')
			.call(d3.axisLeft(yScale).ticks(8).tickFormat(d3.format('.1%')))
			.append('text')
			.attr('transform', 'rotate(-90)')
			.attr('x', -h / 2).attr('y', -40)
			.attr('fill', 'currentColor')
			.attr('text-anchor', 'middle')
			.text('Stock Return');

		// Zero lines
		svg.append('line')
			.attr('x1', xScale(0)).attr('x2', xScale(0))
			.attr('y1', 0).attr('y2', h)
			.attr('stroke', '#ccc').attr('stroke-dasharray', '3,3');

		svg.append('line')
			.attr('x1', 0).attr('x2', w)
			.attr('y1', yScale(0)).attr('y2', yScale(0))
			.attr('stroke', '#ccc').attr('stroke-dasharray', '3,3');

		// Points
		const pointData = stockRet.map((s, i) => ({ x: benchRet[i], y: s }));

		svg.selectAll('.dot')
			.data(pointData)
			.enter()
			.append('circle')
			.attr('class', 'dot')
			.attr('cx', d => xScale(d.x))
			.attr('cy', d => yScale(d.y))
			.attr('r', 3)
			.attr('fill', d => d.x > 0 ? '#4CAF50' : '#F44336')
			.attr('opacity', 0.6);

		// Full regression line
		this._drawRegressionLine(svg, xScale, yScale, xExtent, alpha, beta,
			'#1976D2', '2', null, `β = ${beta.toFixed(4)}`);

		// Asymmetric lines (Tier 2)
		if (this.state.alphaUpside != null && this.state.betaUpside != null) {
			this._drawRegressionLine(svg, xScale, yScale, [0, xExtent[1]],
				this.state.alphaUpside, this.state.betaUpside,
				'#4CAF50', '1.5', '5,3', `β⁺ = ${this.state.betaUpside.toFixed(4)}`);
		}
		if (this.state.alphaDownside != null && this.state.betaDownside != null) {
			this._drawRegressionLine(svg, xScale, yScale, [xExtent[0], 0],
				this.state.alphaDownside, this.state.betaDownside,
				'#F44336', '1.5', '5,3', `β⁻ = ${this.state.betaDownside.toFixed(4)}`);
		}

		// Legend
		this._renderScatterLegend(svg, w);
	}

	_drawRegressionLine(svg, xScale, yScale, xRange, alpha, beta, color, strokeWidth, dashArray, label) {
		const x1 = xRange[0];
		const x2 = xRange[1];
		const y1 = alpha + beta * x1;
		const y2 = alpha + beta * x2;

		const line = svg.append('line')
			.attr('x1', xScale(x1)).attr('x2', xScale(x2))
			.attr('y1', yScale(y1)).attr('y2', yScale(y2))
			.attr('stroke', color)
			.attr('stroke-width', strokeWidth);

		if (dashArray)
			line.attr('stroke-dasharray', dashArray);
	}

	_renderScatterLegend(svg, chartWidth) {
		const legend = svg.append('g')
			.attr('transform', `translate(${chartWidth - 160}, 5)`);

		const items = [
			{ color: '#1976D2', dash: null, label: `β = ${this.state.beta.toFixed(4)}` }
		];
		if (this.state.betaUpside != null)
			items.push({ color: '#4CAF50', dash: '5,3', label: `β⁺ = ${this.state.betaUpside.toFixed(4)}` });
		if (this.state.betaDownside != null)
			items.push({ color: '#F44336', dash: '5,3', label: `β⁻ = ${this.state.betaDownside.toFixed(4)}` });

		items.forEach((item, i) => {
			const g = legend.append('g').attr('transform', `translate(0, ${i * 18})`);
			const line = g.append('line')
				.attr('x1', 0).attr('x2', 20).attr('y1', 6).attr('y2', 6)
				.attr('stroke', item.color).attr('stroke-width', 2);
			if (item.dash) line.attr('stroke-dasharray', item.dash);
			g.append('text').attr('x', 25).attr('y', 10)
				.attr('font-size', '11px').attr('fill', 'currentColor')
				.text(item.label);
		});
	}

	// ══════════════════════════════════════════════
	//  Rolling Beta Line Chart
	// ══════════════════════════════════════════════

	_renderRolling() {
		const container = document.querySelector('.beta-chart-container[data-chart-type="rolling"]');
		if (!container) return;

		const betas = this.state.rollingBetas;
		const dates = this.state.rollingDates.map(d => new Date(d));

		const width = container.clientWidth || 600;
		const height = 350;
		const m = this._margin;
		const w = width - m.left - m.right;
		const h = height - m.top - m.bottom;

		const svg = d3.select(container)
			.append('svg')
			.attr('width', width)
			.attr('height', height)
			.append('g')
			.attr('transform', `translate(${m.left},${m.top})`);

		// Scales
		const xScale = d3.scaleTime()
			.domain(d3.extent(dates))
			.range([0, w]);

		const betaExtent = d3.extent(betas);
		const yPad = (betaExtent[1] - betaExtent[0]) * 0.15 || 0.2;
		const yScale = d3.scaleLinear()
			.domain([betaExtent[0] - yPad, betaExtent[1] + yPad])
			.range([h, 0]);

		// Axes
		svg.append('g')
			.attr('transform', `translate(0,${h})`)
			.call(d3.axisBottom(xScale).ticks(8));

		svg.append('g')
			.call(d3.axisLeft(yScale).ticks(8))
			.append('text')
			.attr('transform', 'rotate(-90)')
			.attr('x', -h / 2).attr('y', -40)
			.attr('fill', 'currentColor')
			.attr('text-anchor', 'middle')
			.text('Rolling β (52w)');

		// P10–P90 band
		if (this.state.p10 != null && this.state.p90 != null) {
			svg.append('rect')
				.attr('x', 0).attr('width', w)
				.attr('y', yScale(this.state.p90))
				.attr('height', yScale(this.state.p10) - yScale(this.state.p90))
				.attr('fill', '#E3F2FD')
				.attr('opacity', 0.5);

			this._drawHLine(svg, yScale, w, this.state.p10, '#90CAF9', '3,3', 'P10');
			this._drawHLine(svg, yScale, w, this.state.p90, '#90CAF9', '3,3', 'P90');
		}

		// Median line
		if (this.state.median != null) {
			this._drawHLine(svg, yScale, w, this.state.median, '#1565C0', null, 'Median');
		}

		// β = 1.0 reference line
		if (yScale.domain()[0] <= 1.0 && yScale.domain()[1] >= 1.0) {
			this._drawHLine(svg, yScale, w, 1.0, '#999', '6,3', 'β=1');
		}

		// Rolling beta line
		const lineGen = d3.line()
			.x((d, i) => xScale(dates[i]))
			.y(d => yScale(d))
			.curve(d3.curveMonotoneX);

		svg.append('path')
			.datum(betas)
			.attr('fill', 'none')
			.attr('stroke', '#1976D2')
			.attr('stroke-width', 2)
			.attr('d', lineGen);

		// Trend line
		if (this.state.trendSlope != null && this.state.trendIntercept != null) {
			const n = betas.length;
			const trendY0 = this.state.trendIntercept;
			const trendY1 = this.state.trendIntercept + this.state.trendSlope * (n - 1);

			svg.append('line')
				.attr('x1', xScale(dates[0]))
				.attr('x2', xScale(dates[n - 1]))
				.attr('y1', yScale(trendY0))
				.attr('y2', yScale(trendY1))
				.attr('stroke', '#FF9800')
				.attr('stroke-width', 1.5)
				.attr('stroke-dasharray', '8,4');
		}

		// Chow break vertical line
		if (this.state.chowBreakDate) {
			const breakDate = new Date(this.state.chowBreakDate);
			const breakX = xScale(breakDate);

			svg.append('line')
				.attr('x1', breakX).attr('x2', breakX)
				.attr('y1', 0).attr('y2', h)
				.attr('stroke', '#D32F2F')
				.attr('stroke-width', 1.5)
				.attr('stroke-dasharray', '5,3');

			svg.append('text')
				.attr('x', breakX + 4).attr('y', 12)
				.attr('font-size', '10px').attr('fill', '#D32F2F')
				.text('Break');
		}

		// Legend
		this._renderRollingLegend(svg, w);
	}

	_drawHLine(svg, yScale, chartWidth, value, color, dashArray, label) {
		const y = yScale(value);

		const line = svg.append('line')
			.attr('x1', 0).attr('x2', chartWidth)
			.attr('y1', y).attr('y2', y)
			.attr('stroke', color)
			.attr('stroke-width', 1);

		if (dashArray) line.attr('stroke-dasharray', dashArray);

		svg.append('text')
			.attr('x', chartWidth + 3).attr('y', y + 4)
			.attr('font-size', '9px').attr('fill', color)
			.text(`${label} (${value.toFixed(2)})`);
	}

	_renderRollingLegend(svg, chartWidth) {
		const legend = svg.append('g')
			.attr('transform', `translate(${chartWidth - 180}, 5)`);

		const items = [
			{ color: '#1976D2', dash: null, width: 2, label: 'Rolling β' },
			{ color: '#1565C0', dash: null, width: 1, label: 'Median' },
			{ color: '#90CAF9', dash: '3,3', width: 1, label: 'P10 / P90' }
		];
		if (this.state.trendSlope != null)
			items.push({ color: '#FF9800', dash: '8,4', width: 1.5, label: 'Trend' });
		if (this.state.chowBreakDate)
			items.push({ color: '#D32F2F', dash: '5,3', width: 1.5, label: 'Structural Break' });

		items.forEach((item, i) => {
			const g = legend.append('g').attr('transform', `translate(0, ${i * 16})`);
			const line = g.append('line')
				.attr('x1', 0).attr('x2', 18).attr('y1', 6).attr('y2', 6)
				.attr('stroke', item.color).attr('stroke-width', item.width);
			if (item.dash) line.attr('stroke-dasharray', item.dash);
			g.append('text').attr('x', 23).attr('y', 10)
				.attr('font-size', '10px').attr('fill', 'currentColor')
				.text(item.label);
		});
	}
}

Tango.registerComponent(BetaCharts);
